<template>
  <div>
    <div
      class="h-2 w-full overflow-hidden rounded-full bg-gray-200"
      role="progressbar"
      :aria-valuenow="answered"
      :aria-valuemin="0"
      :aria-valuemax="total"
      :aria-label="`${answered} of ${total} required questions answered`"
      :data-cy="dataCy"
    >
      <div
        class="h-full rounded-full transition-[width] duration-300 ease-out motion-reduce:transition-none"
        :class="complete ? 'bg-green-600' : 'bg-primary-600'"
        :style="{ width: `${percent}%` }"
      ></div>
    </div>
    <p class="mt-2 text-xs">
      <span class="font-medium text-gray-900" :data-cy="labelDataCy">
        {{ answered }} / {{ total }} required
      </span>
      <span v-if="suffix" class="text-gray-500" :data-cy="suffixDataCy">
        · {{ suffix }}
      </span>
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

// Mirrors the tech app's submission card so the same form reads the same on both
// surfaces.
const props = withDefaults(
  defineProps<{
    answered: number;
    total: number;
    complete?: boolean;
    dataCy?: string;
    labelDataCy?: string;
    suffix?: string;
    suffixDataCy?: string;
  }>(),
  {
    complete: false,
    dataCy: undefined,
    labelDataCy: undefined,
    suffix: undefined,
    suffixDataCy: undefined
  }
);

// A form with no required questions is vacuously complete — the server reports
// `complete: true` for it — so the bar reads full rather than empty. The tech app
// disagrees with itself here: its detail view fills the bar
// (submission-detail.tsx) while its list card leaves it empty
// (submission-card.tsx). Following the detail view, since that is the one that
// agrees with the server.
const percent = computed(() =>
  props.total > 0
    ? Math.min(100, Math.round((props.answered / props.total) * 100))
    : 100
);
</script>
